import type { ModuleRecord } from '@/types/domain';

/**
 * 变更台账（0826 Tony：变更列表改成老 Excel 台账的样式）。
 *
 * 这里管两件事：状态归档（台账的「变更状态」列是 申请/执行/已退回/终止/关闭 五档，
 * 不是库里的十来种状态词），和从记录里取台账单元格的值（新旧键都认）。
 */

export type LedgerBucket = '申请' | '执行' | '已退回' | '终止' | '关闭';

/**
 * 库里的状态 → 台账五档。
 * approved 归「申请」：CCB 批了还没动手，台账口径里它还没进执行。
 * rejected 单列「已退回」，不再并进「终止」（1028 Soft-Gate copy-tail）：打回去重填还要往下走，
 * 看板「未结束」也数它；终止只留给作废。和 ledgerStep 用同一个词。
 * 认不出的状态一律按「申请」——新单最常见，宁可显得保守也不装作已结束。
 */
export function ledgerBucket(status: string | null | undefined): LedgerBucket {
  const value = String(status ?? '').trim().toLowerCase();
  if (['in_progress', 'active'].includes(value)) return '执行';
  if (value === 'rejected') return '已退回';
  if (['cancelled', 'obsolete'].includes(value)) return '终止';
  if (['closed', 'done', 'completed'].includes(value)) return '关闭';
  return '申请';
}

/**
 * 台账上的五步（0828 Tony 定的字面口径）：
 *   第1步-申请 第2步-评估 第3步-批准 第4步-执行 第5步-闭环，完成就是「关闭」。
 *
 * 名字他指定了就照抄，别自作主张改成「提出申请/评估审批/…」——
 * 台账是给不天天用系统的人看的，词对不上他们就得再问一遍。
 * 颜色跟着阶段走：越靠后越"熟"，关闭是绿的、终止是灰的。
 */
export const CHANGE_STEP_SHORT: readonly string[] = ['申请', '评估', '批准', '执行', '闭环'];

/** 台账里这一格长什么样：文字 + 配色键 */
export interface LedgerStepView {
  order: number;
  /** 「第3步 批准」；已关闭的单子直接写「关闭」 */
  label: string;
  /** 配色：apply/assess/approve/execute/close/closed/stopped */
  tone: string;
  /** 这一步做完了、等人推进；终态（关闭/终止）不带这个 */
  ready?: boolean;
}

const STEP_TONES = ['apply', 'assess', 'approve', 'execute', 'close'] as const;

/**
 * 台账里这条单子走到第几步。
 *
 * 几种收尾：
 * - **已关闭**：不写「第5步 闭环」而写「关闭」——单子结束了，说步数没意义；
 * - **作废**：整条标终止，不显示步；
 * - **退回**：标「已退回」，同样不显示步——一条被驳回的写着「第3步」会让人以为还在等批。
 *   它不是终态（打回去重填，还要往下走；看板「未结束」也数它），所以不再和作废一起标终止，
 *   配色借第 1 步那档：回到申请重填（1028 Soft-Gate P2：复评明细把退回显示成「终止」）；
 * - 其余按 current_step（后端给的，老单据按 status 折的初值，和五步条同一套算法）。
 */
export function ledgerStep(record: {
  current_step?: unknown; status?: unknown; normalized?: unknown;
}, stepReady = false): LedgerStepView | null {
  const status = String(record.status ?? '').trim().toLowerCase();
  if (['cancelled', 'obsolete'].includes(status)) {
    return { order: 0, label: '终止', tone: 'stopped' };
  }
  if (status === 'rejected') {
    return { order: 0, label: '已退回', tone: 'apply' };
  }
  if (['closed', 'done', 'completed'].includes(status)) {
    /*
      中途关闭要和正常关闭分得开（0828 Tony:「说明是中途关闭的变更」）。
      两者都是 closed，但一个是走完了、一个是半路停的——
      台账上都写「关闭」，半年后没人分得出哪些单子其实没做完。
    */
    const bag = (record.normalized ?? {}) as Record<string, unknown>;
    if (bag.earlyClose) return { order: 5, label: '中途关闭', tone: 'closed-early' };
    return { order: 5, label: '关闭', tone: 'closed' };
  }
  const order = Number(record.current_step);
  if (!Number.isInteger(order) || order < 1 || order > CHANGE_STEP_SHORT.length) return null;
  return {
    order,
    label: `第${order}步 ${CHANGE_STEP_SHORT[order - 1]!}`,
    /*
      这一步做完了但没人推进（0828 Tony:「台账的状态没有根据实际的推进步骤更新」）。
      台账原来只说「在第 3 步」，说不出这一步是**刚开始**还是**已经做完卡在那**——
      两者在屏幕上长得一模一样，看着就像状态不动。
      和五步条里那个绿空心圈是同一个意思：做完了 ≠ 推进过了。
    */
    ready: stepReady,
    tone: STEP_TONES[order - 1]!,
  };
}

/** 从记录顶层或 normalized 里取一格；旧拼音键给 fallbacks（迁移单据还挂着它们） */
export function ledgerCell(
  record: ModuleRecord,
  key: string,
  ...fallbacks: string[]
): string {
  const bag = (record.normalized ?? {}) as Record<string, unknown>;
  for (const name of [key, ...fallbacks]) {
    const direct = record[name];
    if (direct !== undefined && direct !== null && String(direct).trim() !== '') return String(direct).trim();
    const nested = bag[name];
    if (nested !== undefined && nested !== null && String(nested).trim() !== '') return String(nested).trim();
  }
  return '';
}

/** 台账的费用四列（顺序就是 Excel 的顺序）；权限口径在后端，这里只管摆 */
export const LEDGER_COST_COLUMNS = [
  { key: 'changeExpense', label: '变更费用' },
  { key: 'clientPayback', label: '客户支付' },
  { key: 'costImpact', label: '变更成本' },
  { key: 'priceImpact', label: '变更报价' },
] as const;
